import React, { useState, useEffect } from 'react';
import axios from 'axios';

// UI Components
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Icons
import { 
  Save, 
  Plus, 
  Trash2, 
  Edit, 
  FileText, 
  Package,
  ArrowLeft 
} from 'lucide-react';

// Import API configuration
import { API_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = API_URL;

/**
 * ICS 215 - Operational Planning Worksheet Chart Form
 * 
 * This component manages ICS 215 worksheets for resource coordination and deployment.
 * Features:
 * - Create, Edit, Delete worksheets
 * - Inline editing of resources
 * - Save resources to backend with status indicators
 * - Print ICS 215 reports
 * - Real-time sync via polling
 * 
 * @param {Object} currentExercise - The current exercise context
 * @param {Object} theme - Theme configuration for styling
 */
const ICS215Form = ({ currentExercise, theme }) => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'
  const [worksheets, setWorksheets] = useState([]);
  const [editingWorksheet, setEditingWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingResources, setSavingResources] = useState(false);
  const [resourceSaveStatus, setResourceSaveStatus] = useState(null); // 'saving', 'saved', 'error'

  // Form data for creating/editing worksheets
  const [formData, setFormData] = useState({
    exercise_name: '',
    date_time_from: '',
    date_time_to: ''
  });

  // Load worksheets from backend
  const fetchWorksheets = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const token = secureStorage.getToken();
      const response = await axios.get(`${API}/ics-215-data`, {
        params: { exercise_id: currentExercise.id },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const data = response.data;
      setWorksheets(data.worksheets || []);
      setLastSaved(data.updated_at);
    } catch (error) {
      if (!silent) console.error('Error fetching ICS 215:', error);
      // Fallback to localStorage
      const savedWorksheets = secureStorage.getPreference('ics215_worksheets');
      if (savedWorksheets) {
        setWorksheets(JSON.parse(savedWorksheets));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorksheets();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 15 seconds)
  useEffect(() => {
    if (!currentExercise?.id) return;
    
    const pollInterval = setInterval(() => {
      fetchWorksheets(true);
    }, 15000);
    
    return () => clearInterval(pollInterval);
  }, [currentExercise?.id]);

  // Load resources involved in current exercise
  useEffect(() => {
    fetchResources();
  }, []);

  // Set exercise name when currentExercise changes
  useEffect(() => {
    if (currentExercise?.exercise_name) {
      setFormData(prev => ({
        ...prev,
        exercise_name: currentExercise.exercise_name
      }));
    }
  }, [currentExercise]);

  const fetchResources = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/resources`, { headers });
      // Filter resources that are involved in the current exercise
      const involvedResources = response.data.filter(resource => resource.involved_in_exercise === true);
      setResources(involvedResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    }
  };

  // Save resources to backend
  const saveResources = async () => {
    setSavingResources(true);
    setResourceSaveStatus('saving');
    
    try {
      const token = secureStorage.getToken();
      const headers = token ? { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : { 'Content-Type': 'application/json' };
      
      // Save each resource
      for (const resource of resources) {
        if (resource.isNew || resource.id?.startsWith('temp-')) {
          // Create new resource
          const newResource = {
            ...resource,
            exercise_id: currentExercise?.id,
            involved_in_exercise: true
          };
          delete newResource.isNew;
          delete newResource.id;
          
          await axios.post(`${API}/resources`, newResource, { headers });
        } else {
          // Update existing resource
          await axios.put(`${API}/resources/${resource.id}`, {
            ...resource,
            exercise_id: currentExercise?.id,
            involved_in_exercise: true
          }, { headers });
        }
      }
      
      setResourceSaveStatus('saved');
      setHasUnsavedChanges(false);
      
      // Refresh resources to get proper IDs
      await fetchResources();
      
      // Clear status after 3 seconds
      setTimeout(() => setResourceSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving resources:', error);
      setResourceSaveStatus('error');
      setTimeout(() => setResourceSaveStatus(null), 5000);
    } finally {
      setSavingResources(false);
    }
  };

  // Delete a resource
  const deleteResource = async (index) => {
    const resource = resources[index];
    
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      // If it's a saved resource (not temp), delete from backend
      if (resource.id && !resource.id.startsWith('temp-') && !resource.isNew) {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/resources/${resource.id}`, { headers });
      }
      
      // Remove from local state
      setResources(prev => prev.filter((_, i) => i !== index));
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource. Please try again.');
    }
  };

  // Update a resource field
  const updateResource = (index, field, value) => {
    const updated = [...resources];
    updated[index] = { ...updated[index], [field]: value };
    setResources(updated);
    setHasUnsavedChanges(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    const worksheetData = {
      id: editingWorksheet ? editingWorksheet.id : Date.now().toString(),
      ...formData,
      resources: resources,
      created_at: editingWorksheet ? editingWorksheet.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    let updatedWorksheets;
    if (editingWorksheet) {
      updatedWorksheets = worksheets.map(ws => ws.id === editingWorksheet.id ? worksheetData : ws);
    } else {
      updatedWorksheets = [...worksheets, worksheetData];
    }

    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.put(`${API}/ics-215-data/${currentExercise.id}`, {
        worksheets: updatedWorksheets
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setWorksheets(updatedWorksheets);
      setLastSaved(new Date().toISOString());
      setHasUnsavedChanges(false);
      setView('list');
      setEditingWorksheet(null);
      resetForm();
    } catch (error) {
      console.error('Error saving ICS 215:', error);
      // Fallback to localStorage
      setWorksheets(updatedWorksheets);
      secureStorage.setPreference('ics215_worksheets', JSON.stringify(updatedWorksheets));
      setView('list');
      setEditingWorksheet(null);
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (worksheet) => {
    setEditingWorksheet(worksheet);
    setFormData({
      exercise_name: worksheet.exercise_name,
      date_time_from: worksheet.date_time_from,
      date_time_to: worksheet.date_time_to
    });
    setView('add');
  };

  const handleDelete = async (id) => {
    const updatedWorksheets = worksheets.filter(ws => ws.id !== id);
    
    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.put(`${API}/ics-215-data/${currentExercise.id}`, {
        worksheets: updatedWorksheets
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setWorksheets(updatedWorksheets);
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error('Error deleting ICS 215:', error);
      // Fallback to localStorage
      setWorksheets(updatedWorksheets);
      secureStorage.setPreference('ics215_worksheets', JSON.stringify(updatedWorksheets));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      exercise_name: currentExercise?.exercise_name || '',
      date_time_from: '',
      date_time_to: ''
    });
  };

  const handlePrint = (worksheet) => {
    const printWindow = window.open('', '_blank');
    const currentDateTime = new Date().toLocaleString();
    
    // Calculate totals
    const totalRequest = worksheet.resources.reduce((sum, resource) => sum + (resource.request || 0), 0);
    const totalHave = worksheet.resources.reduce((sum, resource) => sum + (resource.quantity_available || 0), 0);
    const totalNeed = worksheet.resources.reduce((sum, resource) => sum + (resource.quantity_needed || 0), 0);

    const printContent = `
      <html>
        <head>
          <title>ICS 215 - Operational Planning Worksheet Chart</title>
          <style>
            @page { size: landscape; margin: 0.5in; }
            body { font-family: Arial, sans-serif; margin: 15px; color: #333; line-height: 1.3; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
            .header h1 { color: #2563eb; margin: 0; font-size: 20px; }
            .header p { margin: 5px 0 0 0; font-size: 11px; }
            .info-section { margin: 15px 0; }
            .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 15px 0; }
            .info-item { border: 1px solid #ddd; padding: 8px; }
            .info-label { font-weight: bold; color: #2563eb; margin-bottom: 3px; font-size: 11px; }
            .info-value { font-size: 11px; }
            .resource-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 9px; }
            .resource-table th, .resource-table td { border: 1px solid #ddd; padding: 4px; text-align: left; vertical-align: top; }
            .resource-table th { background-color: #2563eb; color: white; font-weight: bold; font-size: 8px; }
            .resource-table td { font-size: 9px; }
            .totals-row { background-color: #f0f8ff; font-weight: bold; }
            .totals-row td { font-size: 10px; }
            .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 215 - Operational Planning Worksheet Chart</h1>
            <p><strong>Generated:</strong> ${currentDateTime}</p>
          </div>
          
          <div class="info-section">
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Exercise Name:</div>
                <div class="info-value">${worksheet.exercise_name || 'N/A'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Date/Time From:</div>
                <div class="info-value">${worksheet.date_time_from ? new Date(worksheet.date_time_from).toLocaleString() : 'N/A'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Date/Time To:</div>
                <div class="info-value">${worksheet.date_time_to ? new Date(worksheet.date_time_to).toLocaleString() : 'N/A'}</div>
              </div>
            </div>
          </div>

          <h3 style="font-size: 12px; margin: 10px 0;">Resources Involved in Current Exercise:</h3>
          
          <table class="resource-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th>Division, Group, or Other</th>
                <th>Work Assignment & Special Inst</th>
                <th>Resource Type</th>
                <th style="text-align: center;">Request</th>
                <th style="text-align: center;">Have</th>
                <th style="text-align: center;">Need</th>
                <th>Overhead Position</th>
                <th>Special Equip & Supplies</th>
                <th>Location</th>
                <th>Requested Arrival</th>
              </tr>
            </thead>
            <tbody>
              ${worksheet.resources.map(resource => `
                <tr>
                  <td>${resource.branch || '-'}</td>
                  <td>${resource.division_group_other || '-'}</td>
                  <td>${resource.work_assignment_special_inst || '-'}</td>
                  <td>${resource.resource_type || '-'}</td>
                  <td style="text-align: center;">${resource.request || 0}</td>
                  <td style="text-align: center;">${resource.quantity_available || 0}</td>
                  <td style="text-align: center;">${resource.quantity_needed || 0}</td>
                  <td>${resource.overhead_position || '-'}</td>
                  <td>${resource.special_equip_supplies || '-'}</td>
                  <td>${resource.location || '-'}</td>
                  <td>${resource.requested_arr_datetime ? new Date(resource.requested_arr_datetime).toLocaleString() : '-'}</td>
                </tr>
              `).join('')}
              <tr class="totals-row">
                <td colspan="4"><strong>TOTALS:</strong></td>
                <td style="text-align: center;"><strong>${totalRequest}</strong></td>
                <td style="text-align: center;"><strong>${totalHave}</strong></td>
                <td style="text-align: center;"><strong>${totalNeed}</strong></td>
                <td colspan="4"></td>
              </tr>
            </tbody>
          </table>

          ${worksheet.resources.length === 0 ? '<p style="text-align: center; color: #666;">No resources are currently involved in this exercise.</p>' : ''}
          
          <div class="footer">
            <div><strong>ICS 215 - Operational Planning Worksheet Chart</strong></div>
            <div>Total Resources: ${worksheet.resources.length} | Request: ${totalRequest} | Have: ${totalHave} | Need: ${totalNeed}</div>
            <div style="margin-top: 8px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Form view for creating/editing worksheets
  if (view === 'add') {
    // Calculate totals for current resources
    const totalRequest = resources.reduce((sum, resource) => sum + (resource.request || 0), 0);
    const totalHave = resources.reduce((sum, resource) => sum + (resource.quantity_available || 0), 0);
    const totalNeed = resources.reduce((sum, resource) => sum + (resource.quantity_needed || 0), 0);

    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>
              {editingWorksheet ? 'Edit ICS 215' : 'Create ICS 215 - Operational Planning Worksheet Chart'}
            </h1>
            <p className={`${theme.colors.textMuted} text-lg`}>
              Operational planning worksheet for resource coordination and deployment
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => {
                // Create a temporary worksheet object for printing
                const tempWorksheet = {
                  ...formData,
                  resources: resources
                };
                handlePrint(tempWorksheet);
              }}
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              <FileText className="h-4 w-4 mr-2" />
              Print ICS 215
            </Button>
            <Button 
              onClick={() => {
                setView('list');
                setEditingWorksheet(null);
                resetForm();
              }}
              variant="outline"
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
          </div>
        </div>

        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-blue-500">Worksheet Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Exercise Name */}
            <div>
              <Label htmlFor="exercise_name" className="text-white">Exercise Name</Label>
              <Input
                id="exercise_name"
                value={formData.exercise_name}
                onChange={(e) => handleInputChange('exercise_name', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Enter exercise name"
              />
            </div>

            {/* Operational Period Title */}
            <div className="text-center py-2">
              <h3 className={`text-xl font-semibold ${theme.colors.textPrimary}`}>Operational Period:</h3>
            </div>

            {/* Date/Time From and To */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_time_from" className="text-white">Date/Time From:</Label>
                <Input
                  id="date_time_from"
                  type="datetime-local"
                  value={formData.date_time_from}
                  onChange={(e) => handleInputChange('date_time_from', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="date_time_to" className="text-white">Date/Time To:</Label>
                <Input
                  id="date_time_to"
                  type="datetime-local"
                  value={formData.date_time_to}
                  onChange={(e) => handleInputChange('date_time_to', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resources Table */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-blue-500">Resources Involved in Current Exercise</CardTitle>
              <CardDescription className={`${theme.colors.textMuted}`}>
                Showing all resources marked as &quot;involved in current exercise&quot;
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {resourceSaveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {resourceSaveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {resourceSaveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button 
                onClick={saveResources}
                disabled={savingResources}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-resources-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button 
                onClick={() => {
                  const newEntry = {
                    id: `temp-${Date.now()}`,
                    branch: '',
                    division_group_other: '',
                    work_assignment_special_inst: '',
                    resource_type: '',
                    identification: '',
                    description: '',
                    request: 0,
                    quantity_available: 0,
                    quantity_needed: 0,
                    overhead_position: '',
                    special_equip_supplies: '',
                    location: '',
                    requested_arr_datetime: '',
                    contact_person: '',
                    contact_phone: '',
                    isNew: true,
                    involved_in_exercise: true
                  };
                  setResources(prev => [...prev, newEntry]);
                  setHasUnsavedChanges(true);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="add-entry-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Entry
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {resources.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="resources-table">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-2 text-blue-400">Branch</th>
                      <th className="text-left p-2 text-blue-400">Division, Group, or Other</th>
                      <th className="text-left p-2 text-blue-400">Work Assigned & Special Inst</th>
                      <th className="text-left p-2 text-blue-400">Resource Type</th>
                      <th className="text-center p-2 text-blue-400">Request</th>
                      <th className="text-center p-2 text-blue-400">Have</th>
                      <th className="text-center p-2 text-blue-400">Need</th>
                      <th className="text-left p-2 text-blue-400">Overhead Position</th>
                      <th className="text-left p-2 text-blue-400">Special Equip & Supplies</th>
                      <th className="text-left p-2 text-blue-400">Location</th>
                      <th className="text-left p-2 text-blue-400">Requested Arrival</th>
                      <th className="text-center p-2 text-blue-400 print:hidden">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map((resource, index) => (
                      <tr key={resource.id || index} className="border-b border-gray-800" data-testid={`resource-row-${index}`}>
                        <td className="p-1">
                          <Input
                            value={resource.branch || ''}
                            onChange={(e) => updateResource(index, 'branch', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-24`}
                            placeholder="Branch"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={resource.division_group_other || ''}
                            onChange={(e) => updateResource(index, 'division_group_other', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-28`}
                            placeholder="Div/Group"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={resource.work_assignment_special_inst || ''}
                            onChange={(e) => updateResource(index, 'work_assignment_special_inst', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-36`}
                            placeholder="Work Assignment"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={resource.resource_type || ''}
                            onChange={(e) => updateResource(index, 'resource_type', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-28`}
                            placeholder="Type"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={resource.request || 0}
                            onChange={(e) => updateResource(index, 'request', parseInt(e.target.value) || 0)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-16 text-center`}
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={resource.quantity_available || 0}
                            onChange={(e) => updateResource(index, 'quantity_available', parseInt(e.target.value) || 0)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-16 text-center`}
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={resource.quantity_needed || 0}
                            onChange={(e) => updateResource(index, 'quantity_needed', parseInt(e.target.value) || 0)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-16 text-center`}
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={resource.overhead_position || ''}
                            onChange={(e) => updateResource(index, 'overhead_position', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-28`}
                            placeholder="Position"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={resource.special_equip_supplies || ''}
                            onChange={(e) => updateResource(index, 'special_equip_supplies', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-28`}
                            placeholder="Equip/Supplies"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={resource.location || ''}
                            onChange={(e) => updateResource(index, 'location', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-24`}
                            placeholder="Location"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="datetime-local"
                            value={resource.requested_arr_datetime || ''}
                            onChange={(e) => updateResource(index, 'requested_arr_datetime', e.target.value)}
                            className={`${theme.colors.inputBackground} ${theme.colors.border} ${theme.colors.textPrimary} text-sm h-8 w-40`}
                          />
                        </td>
                        <td className="p-1 text-center print:hidden">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteResource(index)}
                            className="border-red-500 text-red-500 hover:bg-red-500/10 h-8 w-8 p-0"
                            data-testid={`delete-resource-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="border-t-2 border-blue-500 bg-blue-900/20">
                      <td className="p-2 font-bold text-blue-400" colSpan="4">TOTALS:</td>
                      <td className="p-2 text-center font-bold text-blue-400" data-testid="total-request">{totalRequest}</td>
                      <td className="p-2 text-center font-bold text-blue-400" data-testid="total-have">{totalHave}</td>
                      <td className="p-2 text-center font-bold text-blue-400" data-testid="total-need">{totalNeed}</td>
                      <td className="p-2" colSpan="5"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
                <p className={`${theme.colors.textMuted} text-lg`}>
                  No resources are currently marked as &quot;involved in current exercise&quot;
                </p>
                <p className={`${theme.colors.textMuted} text-sm mt-2`}>
                  Click &quot;Add Entry&quot; to add resources or go to Resources → Edit resources and check &quot;This resource is involved in the current exercise&quot;
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={handleSave}
            className="bg-orange-500 hover:bg-orange-600 text-white flex-1"
            data-testid="save-worksheet-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {editingWorksheet ? 'Update Worksheet' : 'Save Worksheet'}
          </Button>
          <Button 
            onClick={() => {
              setView('list');
              setEditingWorksheet(null);
              resetForm();
            }}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // List view showing saved worksheets
  return (
    <div className="p-6 space-y-6" data-testid="ics215-list-view">
      {/* Header with Sync Status */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 215 - Operational Planning Worksheet Chart</h1>
          <p className={`${theme.colors.textMuted} text-lg`}>
            Manage operational planning worksheets for resource coordination
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <Button 
            onClick={() => {
              if (worksheets.length > 0) {
                handlePrint(worksheets[worksheets.length - 1]);
              }
            }}
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            disabled={worksheets.length === 0}
          >
            <FileText className="h-4 w-4 mr-2" />
            Print ICS 215
          </Button>
          <Button 
            onClick={() => setView('add')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="create-worksheet-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New Worksheet
          </Button>
        </div>
      </div>

      {/* Worksheets List */}
      {worksheets.length > 0 ? (
        <div className="space-y-4">
          {worksheets.map((worksheet) => (
            <Card key={worksheet.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`} data-testid={`worksheet-card-${worksheet.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-blue-500">{worksheet.exercise_name || 'Untitled Worksheet'}</CardTitle>
                    <CardDescription className={`${theme.colors.textMuted} mt-1`}>
                      From: {worksheet.date_time_from ? new Date(worksheet.date_time_from).toLocaleString() : 'Not set'} | 
                      To: {worksheet.date_time_to ? new Date(worksheet.date_time_to).toLocaleString() : 'Not set'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handlePrint(worksheet)}
                      size="sm"
                      variant="outline"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Print ICS 215
                    </Button>
                    <Button
                      onClick={() => handleEdit(worksheet)}
                      size="sm"
                      variant="outline"
                      className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(worksheet.id)}
                      size="sm"
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm">
                  <p className={`${theme.colors.textMuted}`}>
                    Resources: {worksheet.resources?.length || 0} | 
                    Total Request: {worksheet.resources?.reduce((sum, r) => sum + (r.request || 0), 0) || 0} | 
                    Total Have: {worksheet.resources?.reduce((sum, r) => sum + (r.quantity_available || 0), 0) || 0} | 
                    Total Need: {worksheet.resources?.reduce((sum, r) => sum + (r.quantity_needed || 0), 0) || 0}
                  </p>
                  <p className={`${theme.colors.textMuted} text-xs mt-1`}>
                    Created: {new Date(worksheet.created_at).toLocaleString()}
                    {worksheet.updated_at !== worksheet.created_at && (
                      <> | Updated: {new Date(worksheet.updated_at).toLocaleString()}</>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardContent className="text-center py-12">
            <FileText className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-4`}>No Worksheets Created</h3>
            <p className={`${theme.colors.textMuted} mb-6`}>
              Create your first ICS 215 Operational Planning Worksheet Chart to get started.
            </p>
            <Button 
              onClick={() => setView('add')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Worksheet
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ICS215Form;
